import { supabase } from '@/integrations/supabase/client';
import { parseISO, format } from 'date-fns';

export interface WrappedStats {
  totalBooks: number;
  totalPages: number;
  uniqueAuthors: number;
  averageRating: number;
  firstBook?: BookDetail;
  lastBook?: BookDetail;
  shortestBook?: BookDetail;
  longestBook?: BookDetail;
  mostPopularBook?: BookDetail;
  leastPopularBook?: BookDetail;
  topAuthors: Array<{ author: string; count: number }>;
  topGenres: Array<{ genre: string; count: number }>;
  topTags?: Array<{ tag: string; count: number }>;
  monthlyBreakdown: Array<{ month: string; count: number; pages: number }>;
  ratingJourney: Array<{ month: string; averageRating: number; bookCount: number }>;
  readingFormats?: Array<{ format: string; count: number; percentage: number }>;
  favoriteMonth?: { month: string; count: number };
  readingStreak?: number; // Longest consecutive days
}

export interface BookDetail {
  id: string;
  title: string;
  authors: string[];
  pageCount?: number;
  coverUrl?: string;
  rating?: number;
  averageRating?: number;
  ratingCount?: number;
  finishedAt?: string;
}

// Mood mapping based on genres - darker subjects get negative scores, lighter ones positive
const GENRE_MOOD_MAP: Record<string, number> = {
  // Dark/Heavy genres
  'Horror': -1.0,
  'Thriller': -0.8,
  'Mystery': -0.6,
  'True Crime': -0.9,
  'War': -0.8,
  'Tragedy': -0.7,
  'Dark': -0.8,
  'Crime': -0.7,
  'Suspense': -0.6,
  'Gothic': -0.7,
  
  // Neutral genres
  'Fiction': 0,
  'Non-fiction': 0,
  'Biography': 0,
  'History': -0.2,
  'Science': 0.1,
  'Philosophy': -0.1,
  'Business': 0,
  'Politics': -0.2,
  
  // Light/Uplifting genres
  'Romance': 0.8,
  'Comedy': 1.0,
  'Humor': 1.0,
  'Self-help': 0.6,
  'Inspiration': 0.7,
  'Travel': 0.5,
  'Cooking': 0.4,
  'Art': 0.3,
  'Poetry': 0.2,
  'Fantasy': 0.4,
  'Adventure': 0.5,
  'Contemporary': 0.3,
  'Young Adult': 0.4
};

// Normalize author names to handle variations (R.F. Kuang vs R. F. Kuang vs RF Kuang)
function normalizeAuthorName(author: string): string {
  return author
    .toLowerCase()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/\.\s+/g, '.') // Remove space after periods (R. F. -> R.F.)
    .replace(/\s*\.\s*/g, '.') // Normalize spacing around periods
    .trim();
}

function calculateBookMood(subjects?: string[]): number {
  if (!subjects || subjects.length === 0) return 0;
  
  const scores = subjects
    .map(subject => {
      // Try exact match first
      if (GENRE_MOOD_MAP[subject]) return GENRE_MOOD_MAP[subject];
      
      // Try partial match (e.g., "Historical Fiction" contains "History")
      for (const [genre, score] of Object.entries(GENRE_MOOD_MAP)) {
        if (subject.toLowerCase().includes(genre.toLowerCase())) {
          return score;
        }
      }
      
      return 0;
    })
    .filter(score => score !== 0);
  
  if (scores.length === 0) return 0;
  
  return scores.reduce((sum, score) => sum + score, 0) / scores.length;
}

export async function fetchWrappedStats(userId: string, year: number): Promise<WrappedStats> {
  // Fetch all books finished in the specified year
  const { data: userBooks } = await supabase
    .from('user_books')
    .select(`
      id,
      rating_star,
      finished_at,
      started_at,
      reading_format,
      tags,
      book_id,
      books!user_books_book_id_fkey (
        id,
        title,
        authors,
        page_count,
        subjects,
        cover_url,
        cover_cache_url,
        average_rating,
        rating_count
      )
    `)
    .eq('user_id', userId)
    .eq('shelf', 'read')
    .not('finished_at', 'is', null);

  // Filter to the specific year
  const yearBooks = userBooks?.filter(book => {
    const finishedDate = book.finished_at ? new Date(book.finished_at) : null;
    return finishedDate && finishedDate.getFullYear() === year;
  }) || [];

  if (yearBooks.length === 0) {
    return {
      totalBooks: 0,
      totalPages: 0,
      uniqueAuthors: 0,
      averageRating: 0,
      topAuthors: [],
      topGenres: [],
      monthlyBreakdown: [],
      ratingJourney: []
    };
  }

  // Sort by finished_at for first/last
  const sortedBooks = [...yearBooks].sort((a, b) => {
    const dateA = new Date(a.finished_at!).getTime();
    const dateB = new Date(b.finished_at!).getTime();
    return dateA - dateB;
  });

  const firstBook = sortedBooks[0];
  const lastBook = sortedBooks[sortedBooks.length - 1];

  // Calculate basic stats
  const totalBooks = yearBooks.length;
  const totalPages = yearBooks.reduce((sum, book) => sum + (book.books?.page_count || 0), 0);
  
  const uniqueAuthorsSet = new Set<string>();
  yearBooks.forEach(book => {
    book.books?.authors?.forEach(author => {
      uniqueAuthorsSet.add(normalizeAuthorName(author));
    });
  });
  const uniqueAuthors = uniqueAuthorsSet.size;

  const ratingsSum = yearBooks
    .filter(book => book.rating_star)
    .reduce((sum, book) => sum + (book.rating_star || 0), 0);
  const ratedBooksCount = yearBooks.filter(book => book.rating_star).length;
  const averageRating = ratedBooksCount > 0 ? ratingsSum / ratedBooksCount : 0;

  // Find shortest/longest books
  const booksWithPages = yearBooks.filter(book => book.books?.page_count && book.books.page_count > 0);
  const shortestBook = booksWithPages.length > 0
    ? booksWithPages.reduce((shortest, current) => 
        (current.books.page_count || 0) < (shortest.books.page_count || 0) ? current : shortest
      )
    : undefined;
  const longestBook = booksWithPages.length > 0
    ? booksWithPages.reduce((longest, current) => 
        (current.books.page_count || 0) > (longest.books.page_count || 0) ? current : longest
      )
    : undefined;

  // Find most/least popular books (based on community average_rating)
  const booksWithRatings = yearBooks.filter(book => book.books?.rating_count && book.books.rating_count >= 5);
  const mostPopularBook = booksWithRatings.length > 0
    ? booksWithRatings.reduce((most, current) => 
        (current.books.average_rating || 0) > (most.books.average_rating || 0) ? current : most
      )
    : undefined;
  const leastPopularBook = booksWithRatings.length > 0
    ? booksWithRatings.reduce((least, current) => 
        (current.books.average_rating || 0) < (least.books.average_rating || 0) ? current : least
      )
    : undefined;

  // Calculate top authors with normalization
  const authorCounts: Record<string, { count: number; displayName: string }> = {};
  yearBooks.forEach(book => {
    book.books?.authors?.forEach(author => {
      const normalized = normalizeAuthorName(author);
      if (!authorCounts[normalized]) {
        authorCounts[normalized] = { count: 0, displayName: author };
      }
      authorCounts[normalized].count++;
    });
  });
  const topAuthors = Object.values(authorCounts)
    .map(({ displayName, count }) => ({ author: displayName, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Calculate top genres
  const genreCounts: Record<string, number> = {};
  yearBooks.forEach(book => {
    book.books?.subjects?.forEach(subject => {
      genreCounts[subject] = (genreCounts[subject] || 0) + 1;
    });
  });
  const topGenres = Object.entries(genreCounts)
    .map(([genre, count]) => ({ genre, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  // Calculate top tags
  const tagCounts: Record<string, number> = {};
  yearBooks.forEach(book => {
    book.tags?.forEach(tag => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
    });
  });
  const topTags = Object.entries(tagCounts)
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Calculate monthly breakdown
  const monthData: Record<string, { count: number; pages: number; ratings: number[] }> = {};
  yearBooks.forEach(book => {
    if (book.finished_at) {
      const month = format(parseISO(book.finished_at), 'MMM');
      if (!monthData[month]) {
        monthData[month] = { count: 0, pages: 0, ratings: [] };
      }
      monthData[month].count++;
      monthData[month].pages += book.books?.page_count || 0;
      
      if (book.rating_star) {
        monthData[month].ratings.push(book.rating_star);
      }
    }
  });

  const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const monthlyBreakdown = monthOrder
    .filter(month => monthData[month])
    .map(month => ({
      month,
      count: monthData[month].count,
      pages: monthData[month].pages
    }));

  // Calculate rating journey by month
  const ratingJourney = monthOrder
    .filter(month => monthData[month] && monthData[month].ratings.length > 0)
    .map(month => {
      const ratings = monthData[month].ratings;
      const avgRating = ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
      return {
        month,
        averageRating: avgRating,
        bookCount: ratings.length
      };
    });

  // Calculate reading formats distribution
  const formatCounts: Record<string, number> = {};
  yearBooks.forEach(book => {
    const format = book.reading_format || 'unspecified';
    formatCounts[format] = (formatCounts[format] || 0) + 1;
  });
  const totalWithFormat = Object.values(formatCounts).reduce((sum, count) => sum + count, 0);
  const readingFormats = Object.entries(formatCounts)
    .map(([format, count]) => ({
      format,
      count,
      percentage: (count / totalWithFormat) * 100
    }))
    .sort((a, b) => b.count - a.count);

  // Find favorite month (most books read)
  const favoriteMonth = monthlyBreakdown.length > 0
    ? monthlyBreakdown.reduce((max, curr) => curr.count > max.count ? curr : max)
    : undefined;

  return {
    totalBooks,
    totalPages,
    uniqueAuthors,
    averageRating,
    firstBook: firstBook ? {
      id: firstBook.books.id,
      title: firstBook.books.title,
      authors: firstBook.books.authors || [],
      pageCount: firstBook.books.page_count,
      coverUrl: firstBook.books.cover_cache_url || firstBook.books.cover_url,
      rating: firstBook.rating_star,
      finishedAt: firstBook.finished_at
    } : undefined,
    lastBook: lastBook ? {
      id: lastBook.books.id,
      title: lastBook.books.title,
      authors: lastBook.books.authors || [],
      pageCount: lastBook.books.page_count,
      coverUrl: lastBook.books.cover_cache_url || lastBook.books.cover_url,
      rating: lastBook.rating_star,
      finishedAt: lastBook.finished_at
    } : undefined,
    shortestBook: shortestBook ? {
      id: shortestBook.books.id,
      title: shortestBook.books.title,
      authors: shortestBook.books.authors || [],
      pageCount: shortestBook.books.page_count,
      coverUrl: shortestBook.books.cover_cache_url || shortestBook.books.cover_url
    } : undefined,
    longestBook: longestBook ? {
      id: longestBook.books.id,
      title: longestBook.books.title,
      authors: longestBook.books.authors || [],
      pageCount: longestBook.books.page_count,
      coverUrl: longestBook.books.cover_cache_url || longestBook.books.cover_url
    } : undefined,
    mostPopularBook: mostPopularBook ? {
      id: mostPopularBook.books.id,
      title: mostPopularBook.books.title,
      authors: mostPopularBook.books.authors || [],
      pageCount: mostPopularBook.books.page_count,
      coverUrl: mostPopularBook.books.cover_cache_url || mostPopularBook.books.cover_url,
      averageRating: mostPopularBook.books.average_rating,
      ratingCount: mostPopularBook.books.rating_count
    } : undefined,
    leastPopularBook: leastPopularBook ? {
      id: leastPopularBook.books.id,
      title: leastPopularBook.books.title,
      authors: leastPopularBook.books.authors || [],
      pageCount: leastPopularBook.books.page_count,
      coverUrl: leastPopularBook.books.cover_cache_url || leastPopularBook.books.cover_url,
      averageRating: leastPopularBook.books.average_rating,
      ratingCount: leastPopularBook.books.rating_count
    } : undefined,
      topAuthors,
      topGenres,
      topTags: topTags.length > 0 ? topTags : undefined,
      monthlyBreakdown,
      ratingJourney,
      readingFormats,
      favoriteMonth
    };
  }
